/**
 * Einheiten-Umrechnung und Formatierung.
 *
 * Interne Rechen- und Speichereinheit ist durchgaengig **Zentimeter** (cm) als
 * Fliesskommazahl. Pixel existieren nur an der Systemgrenze (Scroll-Events),
 * Meter und Kilometer nur an der Anzeigegrenze. Damit gibt es genau eine Stelle
 * im Code, an der Pixel zu einer physikalischen Laenge werden: pxToCm().
 */

/** Zoll in Zentimetern. Exakt per Definition. */
export const CM_PER_INCH = 2.54

/**
 * Umrechnungsfaktor von CSS-Pixeln in Zentimeter.
 *
 * Ein CSS-Pixel ist keine feste Laenge. Erst devicePixelRatio (Verhaeltnis von
 * Hardware- zu CSS-Pixeln) macht daraus Hardware-Pixel, und erst die Pixeldichte
 * des Displays (ppi = Pixel pro Zoll) macht daraus eine echte Strecke.
 *
 * @param dpr devicePixelRatio des Geraets
 * @param ppi physikalische Pixeldichte des Displays
 */
export function cmPerCssPx(dpr: number, ppi: number): number {
  return (dpr / ppi) * CM_PER_INCH
}

/** Eine Strecke in CSS-Pixeln in Zentimeter umrechnen. */
export function pxToCm(px: number, dpr: number, ppi: number): number {
  return px * cmPerCssPx(dpr, ppi)
}

/** Eine Strecke in Zentimetern in CSS-Pixel umrechnen (fuer Ziel-Distanzen). */
export function cmToPx(cm: number, dpr: number, ppi: number): number {
  return cm / cmPerCssPx(dpr, ppi)
}

export interface FormattedDistance {
  /** Zahlenwert, bereits gerundet, als String mit deutschem Dezimalkomma. */
  value: string
  /** Passende Einheit: cm, m oder km. */
  unit: 'cm' | 'm' | 'km'
}

/**
 * Waehlt selbsttaetig die lesbarste Einheit und formatiert deutsch.
 *
 * Die Schwellen sind so gesetzt, dass die Anzeige beim Hochzaehlen nicht
 * flackert: unter 1 m in cm, darunter/darueber in m, ab 1 km in km.
 */
export function formatDistance(cm: number): FormattedDistance {
  const abs = Math.abs(cm)
  if (abs < 100) {
    return { value: formatNumber(cm, 1), unit: 'cm' }
  }
  if (abs < 100_000) {
    return { value: formatNumber(cm / 100, abs < 10_000 ? 2 : 1), unit: 'm' }
  }
  return { value: formatNumber(cm / 100_000, 3), unit: 'km' }
}

/** Kompakte einzeilige Schreibweise, z. B. fuer Bestenlisten. */
export function formatDistanceInline(cm: number): string {
  const d = formatDistance(cm)
  return `${d.value} ${d.unit}`
}

/**
 * Zeit als Wettkampfangabe: m:ss,hh bzw. ss,hh unter einer Minute.
 * Hundertstel, weil das die uebliche Aufloesung in der Leichtathletik ist.
 */
export function formatTime(ms: number): string {
  const totalHundredths = Math.round(ms / 10)
  const hundredths = totalHundredths % 100
  const totalSeconds = Math.floor(totalHundredths / 100)
  const seconds = totalSeconds % 60
  const minutes = Math.floor(totalSeconds / 60)
  const hh = String(hundredths).padStart(2, '0')
  if (minutes === 0) return `${seconds},${hh}`
  return `${minutes}:${String(seconds).padStart(2, '0')},${hh}`
}

/** Geschwindigkeit in km/h, wie sie ein Tacho anzeigen wuerde. */
export function formatSpeed(cmPerSecond: number): string {
  const kmh = (cmPerSecond * 3600) / 100_000
  return `${formatNumber(kmh, kmh < 10 ? 2 : 1)} km/h`
}

/** Zahl mit deutschem Dezimalkomma und Tausenderpunkten. */
export function formatNumber(value: number, decimals: number): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}
